/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';

import { INssProductFeatureGridData } from './nss-product-feature-grid.data';
import {
    ButtonAlignSelf,
    ButtonSize,
    DescriptionSize,
    DescriptionTextAlign,
    HeadingSize,
    HeadingTextAlign,
    IContentData,
    INssProductFeatureGridProps,
    LayoutAlignItems,
    LayoutAnchor,
    LayoutFlexDirection,
    LayoutJustifyContent,
    SubHeadingSize,
    SubHeadingTextAlign
} from './nss-product-feature-grid.props.autogenerated';

export interface INssProductFeatureGridGlobalProps {
    fontColor?: string;
    fontFamily?: string;
    backgroundImage?: Msdyn365.IImageData;
    backgroundColor?: string;
    link?: string;
}

export interface INssProductFeatureGridLayoutProps {
    height: number;
    columnCount: number;
    cssClassName: string;
}

export interface INssProductFeatureGridHeadingProps {
    text: string;
    bold?: boolean;
    italics?: boolean;
    underline?: boolean;
    size: HeadingSize;
    fontFamily?: string;
    fontColor?: string;
    textAlign: HeadingTextAlign;
    lineHeight?: number;
    letterSpacing?: number;
}

export interface INssProductFeatureGridSubHeadingProps {
    text: string;
    bold?: boolean;
    italics?: boolean;
    underline?: boolean;
    size: SubHeadingSize;
    fontFamily?: string;
    fontColor?: string;
    textAlign: SubHeadingTextAlign;
    lineHeight?: number;
    letterSpacing?: number;
}

export interface INssProductFeatureGridDescriptionProps {
    text: string;
    bold?: boolean;
    italics?: boolean;
    underline?: boolean;
    size: DescriptionSize;
    fontFamily?: string;
    fontColor?: string;
    textAlign: DescriptionTextAlign;
    lineHeight?: number;
    letterSpacing?: number;
}

export interface INssProductFeatureGridButtonProps {
    text: string;
    bold?: boolean;
    italics?: boolean;
    underline?: boolean;
    size: ButtonSize;
    fontFamily?: string;
    fontColor?: string;
    lineHeight?: number;
    letterSpacing?: number;
    backgroundColor?: string;
    link?: string;
    alignSelf: ButtonAlignSelf;
}

export interface INssProductFeatureGridContentLayoutProps {
    width: number;
    height: number;
    zIndex: number;
    anchor: LayoutAnchor;
    padding: string;
    flexDirection: LayoutFlexDirection;
    alignItems: LayoutAlignItems;
    justifyContent: LayoutJustifyContent;
}

export interface INssProductFeatureGridContentProps {
    heading?: INssProductFeatureGridHeadingProps;
    subHeading?: INssProductFeatureGridSubHeadingProps;
    description?: INssProductFeatureGridDescriptionProps;
    button?: INssProductFeatureGridButtonProps;
    backgroundColor?: string;
    foregroundImageUrl?: string;
    layout: INssProductFeatureGridContentLayoutProps;
}

export interface INssProductFeatureGridViewProps extends INssProductFeatureGridProps<INssProductFeatureGridData> {
    global: INssProductFeatureGridGlobalProps;
    layout: INssProductFeatureGridLayoutProps;
    content: INssProductFeatureGridContentProps[];
}

/**
 *
 * NssProductFeatureGrid component
 * @extends {React.PureComponent<INssProductFeatureGridProps<INssProductFeatureGridData>>}
 */
class NssProductFeatureGrid extends React.PureComponent<INssProductFeatureGridProps<INssProductFeatureGridData>> {
    public render(): JSX.Element | null {
        const { config } = this.props;

        const viewProps: INssProductFeatureGridViewProps = {
            ...this.props,
            global: {
                fontColor: config.global?.fontColor,
                fontFamily: config.global?.fontFamily,
                backgroundImage: config.global?.backgroundImage,
                backgroundColor: config.global?.backgroundColor,
                link: config.global?.link?.linkUrl?.destinationUrl
            },
            layout: {
                height: config.layout?.height ?? 40,
                columnCount: config.layout?.columnCount ?? 2,
                cssClassName: config.className ?? ''
            },
            content: []
        };

        if (config.content) {
            for (let i = 0; i < config.content?.length; i++) {
                const configContent: IContentData = config.content[i];
                const viewContent: INssProductFeatureGridContentProps = {
                    backgroundColor: configContent.backgroundColor,
                    foregroundImageUrl: configContent.foregroundImage?.src,
                    layout: {
                        width: configContent.layout?.width ?? 100,
                        height: configContent.layout?.height ?? 100,
                        zIndex: configContent.layout?.zIndex ?? 1,
                        anchor: configContent.layout?.anchor ?? LayoutAnchor.c,
                        padding: configContent.layout?.padding ?? '0',
                        flexDirection: configContent.layout?.flexDirection ?? LayoutFlexDirection.column,
                        alignItems: configContent.layout?.alignItems ?? LayoutAlignItems.start,
                        justifyContent: configContent.layout?.justifyContent ?? LayoutJustifyContent.center
                    }
                };

                if (configContent.heading?.text) {
                    viewContent.heading = {
                        text: configContent.heading.text,
                        bold: configContent.heading.bold,
                        italics: configContent.heading.italics,
                        underline: configContent.heading.underline,
                        size: configContent.heading.size ?? HeadingSize.l,
                        fontFamily: configContent.heading.fontFamily,
                        fontColor: configContent.heading.fontColor,
                        textAlign: configContent.heading.textAlign ?? HeadingTextAlign.center,
                        lineHeight: configContent.heading.lineHeight,
                        letterSpacing: configContent.heading.letterSpacing
                    };
                }

                if (configContent.subHeading?.text) {
                    viewContent.subHeading = {
                        text: configContent.subHeading.text,
                        bold: configContent.subHeading.bold,
                        italics: configContent.subHeading.italics,
                        underline: configContent.subHeading.underline,
                        size: configContent.subHeading.size ?? SubHeadingSize.m,
                        fontFamily: configContent.subHeading.fontFamily,
                        fontColor: configContent.subHeading.fontColor,
                        textAlign: configContent.subHeading.textAlign ?? SubHeadingTextAlign.center,
                        lineHeight: configContent.subHeading.lineHeight,
                        letterSpacing: configContent.subHeading.letterSpacing
                    };
                }

                if (configContent.description?.text) {
                    viewContent.description = {
                        text: configContent.description.text.toString(),
                        bold: configContent.description.bold,
                        italics: configContent.description.italics,
                        underline: configContent.description.underline,
                        size: configContent.description.size ?? DescriptionSize.s,
                        fontFamily: configContent.description.fontFamily,
                        fontColor: configContent.description.fontColor,
                        textAlign: configContent.description.textAlign ?? DescriptionTextAlign.center,
                        lineHeight: configContent.description.lineHeight,
                        letterSpacing: configContent.description.letterSpacing
                    };
                }

                if (configContent.button?.text) {
                    viewContent.button = {
                        text: configContent.button.text,
                        bold: configContent.button.bold,
                        italics: configContent.button.italics,
                        underline: configContent.button.underline,
                        size: configContent.button.size ?? ButtonSize.xs,
                        fontFamily: configContent.button.fontFamily,
                        fontColor: configContent.button.fontColor,
                        lineHeight: configContent.button.lineHeight,
                        letterSpacing: configContent.button.letterSpacing,
                        backgroundColor: configContent.button.backgroundColor,
                        link: configContent.button.link?.linkUrl?.destinationUrl,
                        alignSelf: configContent.button.alignSelf ?? ButtonAlignSelf.start
                    };
                }

                viewProps.content.push(viewContent);
            }
        }

        return this.props.renderView(viewProps);
    }
}

export default NssProductFeatureGrid;
